import React, { useContext, useCallback, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Badge } from '@mantine/core';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexRow, Gap } from 'ui-components/components/flex';

import { AutomationRulesContext } from '../../context';
import ExistingFilterSelector from './existing-filter-selector/';
import { FilterEditorContext } from '../../../../../components/filter-editor/context';
import FiltersEditor from '../../../../../components/filter-editor/components/filters-editor';
import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';

import messages from './messages';
import style from './style.module.less';

const EVERY_ORDER = 'every_order';
const EXISTING_FILTER = 'existing_filter';
const SPECIFIC_CRITERIA = 'specific_criteria';

const RuleTypeSelector = () => {
  const intl = useIntl();
  const { selectedRule, setSelectedRule } = useContext(AutomationRulesContext);
  const { filterSets, getNewTemporaryFilter, setFilterSetWithWorkingFilterId, preconfiguredFilter } = useContext(FilterEditorContext);
  const { filterId } = selectedRule;
  const filter = filterSets[filterId];
  const isHiddenFilter = filter && !filter.active;
  const [backupFilterId, setBackupFilterId] = useState(selectedRule.filterId);

  const removeFilter = useCallback(() => {
    setBackupFilterId(selectedRule.filterId);
    setSelectedRule({ ...selectedRule, filterId: undefined });
  }, [selectedRule, setSelectedRule, setBackupFilterId]);

  const addNewAdHocFilter = useCallback(() => {
    const filterForBackup = filterSets[backupFilterId];
    const isFilterForBackupAnActiveFilter = filterForBackup?.active;
    const newFilterId = getNewTemporaryFilter();
    const hasAnAlreadySavedFilter = filterForBackup && !isFilterForBackupAnActiveFilter;
    const filterIdToUse = hasAnAlreadySavedFilter ? backupFilterId : newFilterId;
    setSelectedRule({ ...selectedRule, filterId: filterIdToUse });
    const filterSetToUse = hasAnAlreadySavedFilter ? filterForBackup : { orderFilterSetId: filterIdToUse, appliedFilters: {} };
    setFilterSetWithWorkingFilterId(filterSetToUse);
  }, [selectedRule, setSelectedRule, getNewTemporaryFilter, backupFilterId, filterSets, setFilterSetWithWorkingFilterId]);

  const setToAnActiveFilter = useCallback(() => {
    const [activeFilter] = Object.values(filterSets).filter((filt) => filt.active);
    const filterForBackupIsActive = filterSets[backupFilterId]?.active;
    setSelectedRule({
      ...selectedRule,
      filterId: filterForBackupIsActive ? backupFilterId : activeFilter?.orderFilterSetId,
    });
  }, [filterSets, selectedRule, setSelectedRule, backupFilterId]);

  useOnComponentMount(() => {
    if (preconfiguredFilter) {
      addNewAdHocFilter();
    }
  });

  let selectedCriteriaValue = EVERY_ORDER;
  if (filterId && isHiddenFilter) selectedCriteriaValue = SPECIFIC_CRITERIA;
  else if (filterId) selectedCriteriaValue = EXISTING_FILTER;

  const hasActiveFilters = useMemo(() => Object.values(filterSets).some((filt) => filt.active), [filterSets]);

  const criteriaOptions = useMemo(
    () => [
      { value: EVERY_ORDER, label: intl.formatMessage(messages.ruleCriteriaTypeEveryOrderOption) },
      { value: EXISTING_FILTER, label: intl.formatMessage(messages.ruleCriteriaTypeExistingFilter), disabled: !hasActiveFilters },
      { value: SPECIFIC_CRITERIA, label: intl.formatMessage(messages.ruleCriteriaTypeSpecificCriteria) },
    ],
    [intl, hasActiveFilters],
  );

  const onCriteriaChange = useCallback(
    (value) => {
      if (value === EVERY_ORDER) removeFilter();
      else if (value === EXISTING_FILTER) setToAnActiveFilter();
      else addNewAdHocFilter();
    },
    [removeFilter, setToAnActiveFilter, addNewAdHocFilter],
  );

  return (
    <div className={style['rule-type-wrap']}>
      <FlexRow className={style['rule-type-label']} gap={Gap.Space8}>
        <Badge className={style['rule-badge']} color='neutral' size='sm' variant='outline'>
          {intl.formatMessage(messages.ruleCriteriaIfBadge)}
        </Badge>
        <SearchableDropdownField
          className={style['rule-type-dropdown']}
          onItemSelected={onCriteriaChange}
          options={criteriaOptions}
          selectedItemValue={selectedCriteriaValue}
        />
        {selectedCriteriaValue === EXISTING_FILTER && <ExistingFilterSelector />}
      </FlexRow>
      {selectedCriteriaValue === SPECIFIC_CRITERIA && (
        <FiltersEditor isTemplate={selectedRule.isTemplate} preconfiguredFilter={preconfiguredFilter} />
      )}
    </div>
  );
};

export default RuleTypeSelector;
